import {
  ChangeEvent,
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import { SxProps, Theme } from "@mui/material";

import { Location, Tag } from "../../../API";
import { useGetLocationsCached } from "../../../pages/customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { useLazyGetPaginatedAllTagsByCustomer } from "../../../pages/system-settings/tags/hooks/useLazyGetPaginatedAllTagsByCustomer";
import {
  MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY,
  getLocationFromLocalStorageByMainStorageKey,
  setLocationToLocalStorage,
} from "../../helpers/getLocationFromLocalStorage";
import { useCustomerIdGuard } from "../../hooks/useCustomerIdGuard";
import {
  AutocompleteOptionType,
  IDisableClearableOption,
} from "../../models/autocomplete";
import { RouteEnum } from "../../models/enums";

import { useLocationFormHandler } from "../../hooks/useLocationFormHandler";
import {
  allLocationsDropdownLoadingVariable,
  allTagLocationsDropdownLoadingVariable,
  useSelectedTagIdDropdown,
  useSelectedTagLocationsDropdown,
} from "../../variables/selectedLocationDropdown";
import { useDialog } from "../dialog/useDialog";
import SearchInput from "../search/SearchInput";
import AllLocationsFilterDropdown from "./AllLocationsFilterDropdown";
import AllLocationsFilterDropdownMenuItem from "./AllLocationsFilterDropdownMenuItem";
import CollapseButton from "./CollapseButton";
import CreateLocationDialog from "./CreateLocationDialog";
import { useSelectedLocationDropdown } from "../../variables/selectedLocationDropdown";

interface AllLocationsFilterDropdownProps {
  setLocation: (locationValue: AutocompleteOptionType) => void;
  setTagLocations?: (locationValue: AutocompleteOptionType[] | null) => void;
  setTagId?: (tagId: string) => void;
  disableClearable?: boolean;
  removeBorder?: boolean;
  enhancedCapabilities?: boolean;
  menuOpened?: boolean;
  size?: "small" | "medium";
  ignoreDefaultValue?: boolean;
  hasError?: boolean;
  hideTags?: boolean;
  selectedTab?: string;
  rememberEmptyValue?: boolean;
  locationDropdownSx?: SxProps<Theme>;
}

const AllLocationsFilterDropdownContainer: FC<
  AllLocationsFilterDropdownProps
> = ({
  setLocation,
  setTagLocations = () => {},
  setTagId = () => {},
  size = "small",
  disableClearable,
  removeBorder,
  enhancedCapabilities,
  menuOpened,
  ignoreDefaultValue,
  hasError,
  hideTags = false,
  rememberEmptyValue = false,
  locationDropdownSx,
}): JSX.Element => {
  const setLocationRef = useRef(setLocation);
  const setTagLocationsRef = useRef(setTagLocations);
  const setTagIdRef = useRef(setTagId);

  useEffect(() => {
    setLocationRef.current = setLocation;

    setTagLocationsRef.current = setTagLocations;

    setTagIdRef.current = setTagId;
  }, [setLocation, setTagLocations, setTagId]);

  const {
    open: openCreateLocationDialog,
    handleDialogOpen,
    handleDialogClose,
  } = useDialog();

  const onSubmitCancelHandler = useCallback(() => {
    handleDialogClose();
  }, [handleDialogClose]);

  const {
    control,
    handleSubmit,
    errors,
    reset,
    loading: saveLocationLoading,
  } = useLocationFormHandler(onSubmitCancelHandler);

  const selectedCustomerId = useCustomerIdGuard();
  const previousCustomerIdRef = useRef<string | null>(selectedCustomerId);

  const { locations, loading: locationsLoading } = useGetLocationsCached();

  const { tags, loading: tagsLoading } = useLazyGetPaginatedAllTagsByCustomer();

  const externalSelectedLocation = useSelectedLocationDropdown();
  const selectedTagId = useSelectedTagIdDropdown();
  const selectedTagLocations = useSelectedTagLocationsDropdown();

  const [selectedValue, setSelectedValue] = useState(
    externalSelectedLocation?.title ?? ""
  );

  useEffect(() => {
    const customerChanged =
      previousCustomerIdRef.current &&
      previousCustomerIdRef.current !== selectedCustomerId;

    if (customerChanged) {
      setSelectedValue("");

      setLocationRef.current(null);

      setTagLocationsRef.current(null);

      setTagIdRef.current("");
    }

    previousCustomerIdRef.current = selectedCustomerId;
  }, [selectedCustomerId]);

  const loading = locationsLoading || tagsLoading;

  useEffect(() => {
    allLocationsDropdownLoadingVariable(loading);

    allTagLocationsDropdownLoadingVariable(loading);
  }, [loading]);

  useEffect(() => {
    if (externalSelectedLocation?.value) {
      const title = externalSelectedLocation?.title ?? "";

      setSelectedValue(prev => (prev !== title ? title : prev));
    }
  }, [externalSelectedLocation?.value, externalSelectedLocation?.title]);

  useEffect(() => {
    if (ignoreDefaultValue) {
      setSelectedValue("");

      setLocationRef.current(null);

      setTagLocationsRef.current(null);

      return;
    }

    if (loading) {
      return;
    }

    if (selectedTagId || (selectedTagLocations?.length ?? 0) > 0) {
      return;
    }

    const externalIsTag =
      externalSelectedLocation?.title?.startsWith("#") ||
      externalSelectedLocation?.value?.includes("#T#");

    if (externalSelectedLocation?.value && !externalIsTag) {
      return;
    }

    if (
      (!locations.length || !enhancedCapabilities) &&
      !externalSelectedLocation
    ) {
      setSelectedValue("");

      setLocationRef.current(null);

      setTagLocationsRef.current(null);

      return;
    }

    const parsedValue = getLocationFromLocalStorageByMainStorageKey();
    const effectiveValue =
      externalSelectedLocation?.value ?? parsedValue?.value;

    let foundValue: IDisableClearableOption | null = null;

    const foundLocation = locations.find(
      location => location.id === effectiveValue
    );

    const foundTag = tags.find(tag => tag.id === effectiveValue);

    if (foundLocation) {
      foundValue = {
        title: foundLocation.name,
        value: foundLocation.id,
      };

      setLocationRef.current(foundValue);
    }

    if (foundTag) {
      foundValue = {
        title: externalSelectedLocation?.title?.startsWith("#")
          ? externalSelectedLocation.title
          : `#${foundTag.tagName}`,
        value: foundTag.id,
      };

      if (selectedTagId !== foundValue.value) {
        handleTagMenuItem(foundValue.title, foundValue.value);
      }
    }

    const isDevicesPage = window.location.pathname === RouteEnum.Devices;

    if (isDevicesPage && foundTag && !foundLocation) {
      const defaultLocation = {
        title: locations[0]?.name ?? "",
        value: locations[0]?.id ?? "",
      };

      if (defaultLocation.value) {
        setLocationRef.current(defaultLocation);
      }

      setTagLocationsRef.current(null);

      setTagIdRef.current("");

      setSelectedValue(defaultLocation.title ?? "");
    } else if (foundValue) {
      setSelectedValue(foundValue.title);
    } else {
      // If we had an external selection (location or tag), but couldn't resolve it yet,
      // do not override it with defaults.
      if (externalSelectedLocation?.value) {
        return;
      }

      // If rememberEmptyValue is true and no stored value, keep empty
      if (rememberEmptyValue && !parsedValue) {
        setSelectedValue("");

        setLocationRef.current(null);

        setTagLocationsRef.current(null);
      } else {
        // Default behavior: select first location
        if (!locations[0]?.id) {
          return;
        }

        const defaultLocation = {
          title: locations[0]?.name ?? "",
          value: locations[0]?.id ?? "",
        };

        setLocationRef.current(defaultLocation);

        setSelectedValue(defaultLocation.title ?? "");
      }
    }
  }, [
    loading,
    selectedCustomerId,
    rememberEmptyValue,
    ignoreDefaultValue,
    enhancedCapabilities,
    locations,
    tags,
    externalSelectedLocation,
    selectedTagId,
    selectedTagLocations,
  ]);

  const [searchValue, setSearchValue] = useState("");

  const handleClear = useCallback((): void => {
    setLocationToLocalStorage(MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY, null);

    setLocation(null);

    setTagLocations(null);

    setSelectedValue("");

    setSearchValue("");
  }, [setLocation, setTagLocations]);

  const [locationsCollapseOpened, setLocationsCollapseOpened] = useState(true);

  const [tagsCollapseOpened, setTagsCollapseOpened] = useState(true);

  const [open, setOpen] = useState(false);

  const collapseClickedRef = useRef(false);

  const handleLocationsCollapse = (): void => {
    collapseClickedRef.current = true;

    setLocationsCollapseOpened(prev => !prev);
  };

  const handleTagsCollapse = (): void => {
    collapseClickedRef.current = true;

    setTagsCollapseOpened(prev => !prev);
  };

  const handleSearchValueChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchValue(event.target.value);
  };

  const clearSearchValue = (): void => {
    setSearchValue("");
  };

  const handleDropdownOpen = (): void => {
    setOpen(true);

    collapseClickedRef.current = true;
  };

  const handleDropdownClose = (): void => {
    if (!collapseClickedRef.current) {
      setOpen(false);

      setSearchValue("");
    }

    collapseClickedRef.current = false;
  };

  const handleLocationMenuItemClick = (
    title?: string | null,
    value?: string | null
  ): void => {
    setOpen(false);

    setTagId("");

    setSelectedValue(title ?? "");

    setTagLocations(null);

    setLocation({
      title: title ?? "",
      value: value ?? "",
    });
  };

  const handleTagMenuItem = (title: string, value: string): void => {
    setOpen(false);

    const selectedTagName = title.replace("#", "");

    if (title) {
      setLocationToLocalStorage(MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY, {
        title,
        value,
      });
    }

    const tagLocations = locations
      .filter(l => {
        return l?.tags?.some(
          tag => tag.tagName.toLowerCase() === selectedTagName.toLowerCase()
        );
      })
      .map(l => {
        return {
          title: l?.name ?? "",
          value: l?.id ?? "",
        };
      });

    setSelectedValue(title ?? "");

    setLocation(null);

    setTagLocations(tagLocations);

    setTagId(value);
  };

  const handleOnBackdropClick: MouseEventHandler<HTMLElement> = (
    event
  ): void => {
    const className = (event.target as HTMLElement)?.getAttribute("class");

    if (!className) {
      return;
    }

    if (className.includes("MuiBackdrop-root")) {
      setOpen(false);

      setSearchValue("");
    }
  };

  const openDialog = (): void => {
    setOpen(false);

    handleDialogOpen();
  };

  const closeDialog = (): void => {
    handleDialogClose();

    reset();
  };

  const mappedTags = useMemo(() => {
    return tags.map(tag => {
      return {
        ...tag,
        tagName: `#${tag?.tagName}`,
      };
    });
  }, [tags]);

  return (
    <FormControl sx={{ width: "100%" }}>
      <AllLocationsFilterDropdown
        selectedValue={selectedValue}
        open={open}
        sx={locationDropdownSx}
        handleDropdownOpen={handleDropdownOpen}
        handleDropdownClose={handleDropdownClose}
        handleOnBackdropClick={handleOnBackdropClick}
        handleClear={handleClear}
        disableClearable={disableClearable}
        removeBorder={removeBorder}
        menuOpened={menuOpened}
        size={size}
        showPlaceHolder={!selectedValue}
        hasError={hasError}
      >
        <SearchInput
          sx={{
            padding: "0.5em -1em",
            marginLeft: "1em",
          }}
          variant="standard"
          size="small"
          value={searchValue}
          searchValue={searchValue}
          handleSearchValueChange={handleSearchValueChange}
          clearSearchValue={clearSearchValue}
        />

        <Box
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {!hideTags && (
            <>
              <CollapseButton
                label="Tags"
                opened={tagsCollapseOpened}
                handleCollapse={handleTagsCollapse}
              />
              <Collapse in={tagsCollapseOpened}>
                {mappedTags
                  .filter(tag => {
                    const tagName = tag?.tagName ?? "";

                    return tagName
                      .toLowerCase()
                      .includes(searchValue.toLowerCase());
                  })
                  .map((tag: Tag) => (
                    <AllLocationsFilterDropdownMenuItem
                      key={tag.id}
                      entityId={tag.id ?? ""}
                      entityName={tag.tagName ?? ""}
                      onMenuItemClick={handleTagMenuItem}
                    />
                  ))}
              </Collapse>
            </>
          )}

          <CollapseButton
            label="Locations"
            opened={locationsCollapseOpened}
            handleCollapse={handleLocationsCollapse}
          />
          <Collapse in={locationsCollapseOpened}>
            {locations
              .filter((l: Location) => {
                const locationName = l?.name ?? "";

                return locationName
                  .toLowerCase()
                  .includes(searchValue.toLowerCase());
              })
              .map((l: Location) => {
                return (
                  <AllLocationsFilterDropdownMenuItem
                    key={l.id}
                    entityId={l.id ?? ""}
                    entityName={l.name ?? ""}
                    onMenuItemClick={handleLocationMenuItemClick}
                  />
                );
              })}
          </Collapse>
        </Box>

        {enhancedCapabilities && (
          <Box
            sx={{
              width: "100%",
              height: "36px",
              display: "flex",
              justifyContent: "flex-end",
              boxShadow: "0px 4px 20px 0px #0816331A",
            }}
          >
            <Button
              sx={{
                height: "36px",
                textTransform: "none",
                padding: "8px",
                color: (theme): string => theme.palette.text.primary,
                backgroundColor: "inherit",
                "&:hover": {
                  backgroundColor: "inherit",
                },
                borderRadius: 0,
              }}
              disableRipple
              variant="text"
              startIcon={<AddIcon />}
              color="primary"
              onClick={openDialog}
            >
              New location
            </Button>
          </Box>
        )}
      </AllLocationsFilterDropdown>

      <CreateLocationDialog
        open={openCreateLocationDialog}
        onClose={closeDialog}
        handleSubmit={handleSubmit}
        control={control}
        errors={errors}
        loading={saveLocationLoading}
      />
    </FormControl>
  );
};

export default AllLocationsFilterDropdownContainer;
